import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { extractErrorMessage } from '../../../api/errors';
import { marketplaceApi } from '../../../api/marketplace';
import { organisationsApi } from '../../../api/organisations';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Input from '../../../components/ui/Input';

const UNITS = [
  { value: 'kg', label: 'kg' },
  { value: 'quintal', label: 'quintal' },
  { value: 'tonne', label: 'tonne' },
];

const QUALITY_GRADES = [
  { value: '', label: '— not graded —' },
  { value: 'A', label: 'Grade A (Premium)' },
  { value: 'FAQ', label: 'FAQ (Fair Average Quality)' },
  { value: 'B', label: 'Grade B (Standard)' },
  { value: 'C', label: 'Grade C (Sub-standard)' },
  { value: 'organic', label: 'Organic certified' },
];

const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-amber-100 text-amber-700',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const blankListing = () => ({
  crop: '',
  title: '',
  description: '',
  quantity_available: '',
  unit: 'quintal',
  price_per_unit: '',
  min_order_quantity: '1',
  available_from: todayIso(),
  available_until: '',
  status: 'draft',
  quality_grade: '',
  moisture_pct: '',
});

export default function Listings() {
  const [listings, setListings] = useState([]);
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankListing);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const load = async () => {
    try {
      const [l, c] = await Promise.all([
        marketplaceApi.myListings(),
        organisationsApi.listCrops(),
      ]);
      setListings(l);
      setCrops(c);
      setError('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(blankListing());
    setShowForm(true);
    setFormError('');
  };

  const startEdit = (listing) => {
    setEditingId(listing.id);
    setForm({
      crop: listing.crop,
      title: listing.title,
      description: listing.description || '',
      quantity_available: String(listing.quantity_available),
      unit: listing.unit,
      price_per_unit: String(listing.price_per_unit),
      min_order_quantity: String(listing.min_order_quantity),
      available_from: listing.available_from,
      available_until: listing.available_until || '',
      status: listing.status,
      quality_grade: listing.quality_grade || '',
      moisture_pct: listing.moisture_pct != null ? String(listing.moisture_pct) : '',
    });
    setShowForm(true);
    setFormError('');
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(blankListing());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    try {
      const payload = {
        ...form,
        available_until: form.available_until || null,
        quality_grade: form.quality_grade || '',
        moisture_pct: form.moisture_pct === '' ? null : form.moisture_pct,
      };
      if (editingId) {
        await marketplaceApi.updateListing(editingId, payload);
      } else {
        await marketplaceApi.createListing(payload);
      }
      cancelForm();
      await load();
    } catch (err) {
      setFormError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async (listing) => {
    if (!window.confirm(`Archive "${listing.title}"? Buyers won't see it anymore.`)) return;
    try {
      await marketplaceApi.updateListing(listing.id, { status: 'archived' });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  };

  const handlePublish = async (listing) => {
    try {
      await marketplaceApi.updateListing(listing.id, { status: 'published' });
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  };

  const handleDelete = async (listing) => {
    if (!window.confirm(`Delete "${listing.title}"? This can't be undone.`)) return;
    try {
      await marketplaceApi.deleteListing(listing.id);
      await load();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Loading listings…</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Listings</h1>
          <p className="mt-1 text-sm text-gray-600">
            Crops your FPO has aggregated and is offering to bulk buyers. Publish a listing to make it visible.
          </p>
        </div>
        <Button onClick={showForm ? cancelForm : startCreate}>
          {showForm ? 'Cancel' : '+ New listing'}
        </Button>
      </div>

      {showForm && (
        <Card className="p-6">
          <h2 className="text-base font-semibold text-gray-900">
            {editingId ? 'Edit listing' : 'New listing'}
          </h2>
          <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1 sm:col-span-2">
              <label className="text-sm font-medium text-gray-700">Crop</label>
              <select
                value={form.crop}
                onChange={(e) => setForm({ ...form, crop: e.target.value })}
                required
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
              >
                <option value="">— pick a crop —</option>
                {crops.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.category})
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              placeholder="Premium Tomatoes — Grade A — March harvest"
              className="sm:col-span-2"
            />

            <div className="flex flex-col gap-1 sm:col-span-2">
              <label className="text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
                placeholder="Hand-picked, vine-ripened, transport-ready."
              />
            </div>

            <Input
              label="Quantity available"
              type="number"
              step="0.01"
              min="0"
              value={form.quantity_available}
              onChange={(e) => setForm({ ...form, quantity_available: e.target.value })}
              required
              placeholder="500"
            />

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Unit</label>
              <select
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
              >
                {UNITS.map((u) => (
                  <option key={u.value} value={u.value}>{u.label}</option>
                ))}
              </select>
            </div>

            <Input
              label="Price per unit (₹)"
              type="number"
              step="0.01"
              min="0"
              value={form.price_per_unit}
              onChange={(e) => setForm({ ...form, price_per_unit: e.target.value })}
              required
              placeholder="35.00"
            />

            <Input
              label="Min order quantity"
              type="number"
              step="0.01"
              min="0"
              value={form.min_order_quantity}
              onChange={(e) => setForm({ ...form, min_order_quantity: e.target.value })}
              required
              placeholder="50"
            />

            <Input
              label="Available from"
              type="date"
              value={form.available_from}
              onChange={(e) => setForm({ ...form, available_from: e.target.value })}
              required
            />

            <Input
              label="Available until (optional)"
              type="date"
              value={form.available_until}
              onChange={(e) => setForm({ ...form, available_until: e.target.value })}
            />

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
              >
                <option value="draft">Draft (only you see it)</option>
                <option value="published">Published (buyers can see it)</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Quality grade</label>
              <select
                value={form.quality_grade}
                onChange={(e) => setForm({ ...form, quality_grade: e.target.value })}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
              >
                {QUALITY_GRADES.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </div>

            <Input
              label="Moisture % (optional)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.moisture_pct}
              onChange={(e) => setForm({ ...form, moisture_pct: e.target.value })}
              placeholder="12.5"
            />

            {formError && <p className="text-sm text-red-600 sm:col-span-2">{formError}</p>}

            <div className="flex gap-3 sm:col-span-2">
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : editingId ? 'Save changes' : 'Create listing'}
              </Button>
              <Button type="button" variant="secondary" onClick={cancelForm}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {listings.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-gray-500">No listings yet. Create your first crop offering above.</p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {listings.map((l) => (
            <Card key={l.id} className="flex flex-col gap-3 p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${STATUS_BADGE[l.status]}`}>
                    {l.status}
                  </span>
                  <h3 className="mt-2 text-base font-semibold text-gray-900">{l.title}</h3>
                  <p className="mt-0.5 text-xs text-gray-500">{l.crop_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-green-800">
                    ₹{Number(l.price_per_unit).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">per {l.unit}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                <div>
                  <p className="font-medium text-gray-500">Available</p>
                  <p>{Number(l.quantity_available).toFixed(2)} {l.unit}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-500">Min order</p>
                  <p>{Number(l.min_order_quantity).toFixed(2)} {l.unit}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-500">From</p>
                  <p>{l.available_from}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-500">Until</p>
                  <p>{l.available_until || '—'}</p>
                </div>
              </div>

              {l.description && (
                <p className="text-xs text-gray-600 line-clamp-3">{l.description}</p>
              )}

              <div className="mt-auto flex flex-wrap gap-2 pt-2">
                <Link
                  to={`/app/listings/${l.id}`}
                  className="text-xs font-semibold text-green-700 hover:text-green-800"
                >
                  {l.images && l.images.length ? `Manage (${l.images.length} 📷)` : 'Manage / add photos'}
                </Link>
                <button
                  type="button"
                  onClick={() => startEdit(l)}
                  className="text-xs font-semibold text-green-700 hover:text-green-800"
                >
                  Edit
                </button>
                {l.status === 'draft' && (
                  <button
                    type="button"
                    onClick={() => handlePublish(l)}
                    className="text-xs font-semibold text-green-700 hover:text-green-800"
                  >
                    Publish
                  </button>
                )}
                {l.status !== 'archived' && (
                  <button
                    type="button"
                    onClick={() => handleArchive(l)}
                    className="text-xs font-semibold text-amber-700 hover:text-amber-800"
                  >
                    Archive
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDelete(l)}
                  className="ml-auto text-xs text-red-600 hover:text-red-700"
                >
                  Delete
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
